import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function Repartidores() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [nombre, setNombre] = useState('');
  const [loading, setLoading] = useState(false);

  const fetch = async () => {
    try {
      const res = await API.get('/catalogs/drivers');
      setItems(res.data);
    } catch (err) {
      toast.error('Error cargando repartidores');
    }
  };

  useEffect(() => { fetch(); }, []);

  const handleSave = async () => {
    if (!nombre.trim()) { toast.error('Ingresa un nombre'); return; }
    setLoading(true);
    try {
      if (editing) {
        await API.put(`/catalogs/drivers/${editing.id}`, { nombre });
        toast.success('Repartidor actualizado');
      } else {
        await API.post('/catalogs/drivers', { nombre });
        toast.success('Repartidor creado');
      }
      setOpen(false); setEditing(null); setNombre('');
      fetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este repartidor?')) return;
    try {
      await API.delete(`/catalogs/drivers/${id}`);
      toast.success('Repartidor eliminado');
      fetch();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Repartidores</h1>
          <p className="text-sm text-muted-foreground">Gestión del catálogo de repartidores</p>
        </div>
        <Button onClick={() => { setEditing(null); setNombre(''); setOpen(true); }} className="bg-[#0F6E87] hover:bg-[#0C5D73]" data-testid="table-create-button">
          <Plus className="h-4 w-4 mr-1" /> Nuevo
        </Button>
      </div>

      <Card className="bg-white border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30">
                <TableHead>Nombre</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && (
                <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-8">No hay repartidores registrados</TableCell></TableRow>
              )}
              {items.map(item => (
                <TableRow key={item.id} className="hover:bg-muted/40">
                  <TableCell className="font-medium">{item.nombre}</TableCell>
                  <TableCell>{item.activo !== false ? <span className="text-emerald-600 text-xs">Activo</span> : <span className="text-red-600 text-xs">Inactivo</span>}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditing(item); setNombre(item.nombre); setOpen(true); }}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete(item.id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar' : 'Nuevo'} Repartidor</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Nombre</label>
              <Input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Nombre del repartidor" className="mt-1" data-testid="field-nombre" autoFocus />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={loading} className="bg-[#0F6E87] hover:bg-[#0C5D73]" data-testid="entity-form-submit-button">{loading ? 'Guardando...' : 'Guardar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
